// src/services/curatedFikaAdminService.js
// Curated Fika Admin Service - Phase 1 Service Layer
// API client for curated fika admin management operations

class CuratedFikaAdminService {
    constructor() {
      this.baseUrl = import.meta.env.VITE_SUPABASE_URL;
      this.initialized = false;
    }
  
    // Initialize service
    async initialize() {
      try {
        this.initialized = true;
        console.log('✅ Curated Fika Admin service initialized');
        return true;
      } catch (error) {
        console.error('❌ Curated Fika Admin service initialization failed:', error);
        this.initialized = false;
        return false;
      }
    }
  
    // Core API call method
    async origcallAPI(action, data = null, filters = {}, pagination = {}) {
      try {
        if (!this.initialized) {
          await this.initialize();
        }
  
        console.log(`🔧 Curated Fika Admin API call: ${action}`, { 
          hasData: !!data, 
          hasFilters: Object.keys(filters).length > 0 
        });
  
        const response = await fetch(`${this.baseUrl}/functions/v1/curated-fika-admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ 
            action, 
            data, 
            filters,
            limit: pagination.limit || 50,
            offset: pagination.offset || 0
          })
        });
  
        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.error || `API call failed for ${action}`);
        }
  
        console.log(`✅ Curated Fika Admin API success: ${action}`);
        return { success: true, ...result };
        // return { success: true, data: result.data, message: result.message };
  
      } catch (error) {
        console.error(`❌ Curated Fika Admin API error (${action}):`, error);
        return { success: false, error: error.message };
      }
    }

    // 11-16-25
    // In curatedFikaAdminService.js - Update callAPI method

async callAPI(action, data = null, filters = {}, pagination = {}) {
  try {
    if (!this.initialized) {
      await this.initialize();
    }

    // Determine which endpoint to use
    const isEntrepreneurAction = action.includes('entrepreneur');
    const endpoint = isEntrepreneurAction 
      ? 'curated-fika-entrepreneurs-admin'
      : 'curated-fika-admin';

    console.log(`🔧 API call: ${action} -> ${endpoint}`, { 
      hasData: !!data, 
      hasFilters: Object.keys(filters).length > 0 
    });

    const response = await fetch(`${this.baseUrl}/functions/v1/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ 
        action, 
        data, 
        filters,
        limit: pagination.limit || 50,
        offset: pagination.offset || 0
      })
    });

    const result = await response.json();
    
    if (!response.ok || !result.success) {
      throw new Error(result.error || `API call failed for ${action}`);
    }

    console.log(`✅ API success: ${action}`);
    return { success: true, ...result };

  } catch (error) {
    console.error(`❌ API error (${action}):`, error);
    return { success: false, error: error.message };
  }
}

   

    
    // New Services

    async updateSessionDetails(sessionId, updates) {
      return await this.callAPI('update_session_details', {
        sessionId,
        updates
      });
    }

    // 9/17/25
    // =====================================================
    // FEEDBACK MANAGEMENT
    // =====================================================

    async getFeedback(filters = {}, pagination = {}) {
      return await this.callAPI('get_feedback', null, filters, pagination);
    }

    async getFeedbackStats() {
      return await this.callAPI('get_feedback_stats');
    }

    // 9-20-25
    // Add this method to the CuratedFikaAdminService class
    async deleteSession(sessionId, options = {}) {
      return await this.callAPI('delete_session', {
        sessionId,
        options
      });
    }

    async deleteSessionWithValidation(sessionId, forceDelete = false, acknowledgeConfirmations = false, sendCancellationEmails = false) {
      return await this.deleteSession(sessionId, {
        force_delete: forceDelete,
        acknowledged_confirmations: acknowledgeConfirmations,
        send_cancellation_emails: sendCancellationEmails
      });
    }

   // 9-22-25
   async deleteParticipant(participantId, options = {}) {
    return await this.callAPI('delete_participant', {
      participantId,
      options
    });
  }
   
  // 9-22-25
  async addParticipantToSession(sessionId, applicationId, options = {}) {
    return await this.callAPI('add_participant_to_session', {
      sessionId,
      applicationId,
      options
    });
  }

  // 9-22-25
  async cancelParticipantAttendance(participantId, cancellationReason = '') {
    return await this.callAPI('cancel_participant_attendance', {
      participantId,
      cancellationReason
    });
  }

  // 9-23-25
  async sendSessionReminder(sessionId) {
    return await this.callAPI('send_session_reminder', {
      sessionId
    });
  }

  // 9-25-25
  async sendConfirmationNudges(sessionId = null) {
    return await this.callAPI('send_confirmation_nudges', { sessionId });
  }
    
    async getSessionDetails(sessionId) {
      return await this.callAPI('get_session_details', { sessionId });
    }
    
    async updateParticipantStatus(participantId, status) {
      return await this.callAPI('update_participant_status', {
        participantId,
        status
      });
    }

  // 9-26-25
  async runAIMatchingV2(options = {}) {
    return await this.callAPI('run_ai_matching_v2', options);
  }


  // 9-30-25
  async getApplicationByEmail(email, formatType = 'curated_fika') {
    return await this.callAPI('get_application_by_email', { email, format_type: formatType });
  }

  // 10-1-25
  async sendSessionAccessCode(email) {
    return await this.callAPI('send_session_access_code', { email });
  }

  async getSessionsByEmailWithCode(email, code) {
    return await this.callAPI('get_sessions_by_email_with_code', { email, code });
  }

  // 10-15-25
  async createOpenSession(sessionData) {
    try {
      return await this.callAPI('create_open_session', { 
        sessionData 
      });
    } catch (error) {
      console.error('Error creating open session:', error);
      throw error;
    }
  }
  
  // ✅ Keep existing method unchanged
  async createSessionFromApplications(applicationIds, sessionData) {
    try {
      return await this.callAPI('create_session_from_applications', {
        applicationIds,
        sessionData
      });
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

 

    // NEW: Venue management methods, 9/16/25
    async getVenues(filters = {}) {
      return await this.callAPI('get_venues', null, filters);
    }

    async addVenue(venueData) {
      return await this.callAPI('add_venue', { venueData });
    }
  
    // =====================================================
    // STATISTICS AND OVERVIEW
    // =====================================================
  
    async getAdminStats() {
      return await this.callAPI('get_admin_stats');
    }
  
    // =====================================================
    // APPLICATION MANAGEMENT
    // =====================================================
  
    async getApplications(filters = {}, pagination = {}) {
      const result = await this.callAPI('get_applications', null, filters, pagination);
      return result;
    }
  
    async getApplicationDetails(applicationId) {
      return await this.callAPI('get_application_details', { applicationId });
    }
  
    async updateApplicationStatus(applicationId, status, notes = '') {
      return await this.callAPI('update_application_status', {
        applicationId,
        status,
        notes
      });
    }
  
    async bulkUpdateApplications(applicationIds, updates) {
      console.log('🚧 Bulk update applications not yet implemented:', { applicationIds, updates });
      return { success: false, error: 'Bulk update not yet implemented' };
    }
  
    // =====================================================
    // SESSION MANAGEMENT
    // =====================================================
  
    async getSessions(filters = {}) {
      return await this.callAPI('get_sessions', null, filters);
    }
  
    async createSession(sessionData) {
      return await this.callAPI('create_session', sessionData);
    }
  
    async createSessionFromApplications(applicationIds, sessionData) {
      return await this.callAPI('create_session_from_applications', {
        applicationIds,
        sessionData
      });
    }
  
    

     // REPLACE the existing updateSessionStatus method with this:
     async updateSessionStatus(sessionId, status, notes = '') {
      return await this.callAPI('update_session_status', {
        sessionId,
        status,
        notes
      });
    }
  
    // =====================================================
    // AI MATCHING AND AUTOMATION
    // =====================================================
  
    async findCompatibleApplications(criteria) {
      return await this.callAPI('find_compatible_applications', criteria);
    }
  
    async origrunAIMatching(options = {}) {
      return await this.callAPI('run_ai_matching', options);
    }

    async runAIMatching(options = {}) {
      // Validate that geographic filters are provided
      if (!options.city || !options.country) {
        return { 
          success: false, 
          error: 'City and country must be specified for AI matching' 
        };
      }
    
      return await this.callAPI('run_ai_matching', options);
    }
  
    async previewMatching(criteria) {
      // Preview what would be matched without actually creating sessions
      const result = await this.findCompatibleApplications(criteria);
      return result;
    }
  
    async calculateCompatibilityMatrix(applications) {
      // Calculate compatibility scores between all applications
      const matrix = {};
      
      for (let i = 0; i < applications.length; i++) {
        for (let j = i + 1; j < applications.length; j++) {
          const app1 = applications[i];
          const app2 = applications[j];
          const compatibility = this.calculateCompatibilityScore(app1, app2);
          
          const key = `${app1.id}-${app2.id}`;
          matrix[key] = compatibility;
        }
      }
      
      return { success: true, data: matrix };
    }
  
    // =====================================================
    // COMMUNICATION AND NOTIFICATIONS
    // =====================================================
  
    async sendSessionInvitations(sessionId) {
      return await this.callAPI('send_session_invitations', { sessionId });
    }

    async sendFeedbackEmails(sessionId) {
      return await this.callAPI('send_feedback_emails', { sessionId });
    }

    // Deep Fika feedback
    async getDeepFikaFeedback(pagination = {}) {
      return await this.callAPI('get_deep_fika_feedback', null, {}, pagination);
    }

    // Deep Fika mutual acceptance
    async sendDeepFikaInvitations(applicationIds, adminNote = '') {
      return await this.callAPI('send_deep_fika_invitations', { applicationIds, adminNote });
    }

    async sendDeepFikaFeedbackEmail(sessionId) {
      return await this.callAPI('send_deep_fika_feedback', { sessionId });
    }

    async sendBulkNotifications(recipientIds, notificationType, customMessage = '') {
      console.log('🚧 Bulk notifications not yet implemented:', { 
        recipientIds, 
        notificationType, 
        customMessage 
      });
      return { success: false, error: 'Bulk notifications not yet implemented' };
    }
  
    async sendApplicationStatusUpdate(applicationId, status, personalizedMessage = '') {
      return await this.callAPI('send_application_status_update', {
        applicationId,
        status,
        personalizedMessage
      });
    }
  
    // =====================================================
    // REPORTING AND ANALYTICS
    // =====================================================
  
    async getMatchingReport(dateRange = {}) {
      console.log('🚧 Matching report not yet implemented:', dateRange);
      return { success: false, error: 'Matching report not yet implemented' };
    }
  
    async getSessionAnalytics(sessionId = null) {
      console.log('🚧 Session analytics not yet implemented:', sessionId);
      return { success: false, error: 'Session analytics not yet implemented' };
    }
  
    async getRevenueReport(dateRange = {}) {
      console.log('🚧 Revenue report not yet implemented:', dateRange);
      return { success: false, error: 'Revenue report not yet implemented' };
    }
  
    async exportData(dataType, filters = {}) {
      console.log('🚧 Data export not yet implemented:', { dataType, filters });
      return { success: false, error: 'Data export not yet implemented' };
    }
  
    // =====================================================
    // UTILITY METHODS
    // =====================================================
  
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  
  

    formatDateTime(dateString, city = 'Tokyo') {
      if (!dateString) return 'N/A';
      
      const timezoneMap = {
        'Tokyo': 'Asia/Tokyo',
        'Osaka': 'Asia/Tokyo'
      };
      
      const timezone = timezoneMap[city] || 'Asia/Tokyo';
      
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timezone
      });
    }
  
    getStatusColor(status) {
      const colors = {
        submitted: 'bg-blue-100 text-blue-800 border-blue-200',
        under_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        approved: 'bg-green-100 text-green-800 border-green-200',
        rejected: 'bg-red-100 text-red-800 border-red-200',
        waitlist: 'bg-purple-100 text-purple-800 border-purple-200',
        
        // Session statuses
        scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
        confirmed: 'bg-green-100 text-green-800 border-green-200',
        in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
        completed: 'bg-purple-100 text-purple-800 border-purple-200',
        cancelled: 'bg-red-100 text-red-800 border-red-200'
      };
      return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
  
    getInterestDisplay(interests) {
      if (!interests || !Array.isArray(interests)) return [];
      
      const displays = {
        'Technology & Startups': '💻 Technology & Startups',
        'Arts & Culture': '🎨 Arts & Culture',
        'Travel & Adventure': '✈️ Travel & Adventure',
        'Food & Culinary': '🍽️ Food & Culinary',
        'Health & Wellness': '🧘 Health & Wellness',
        'Books & Literature': '📚 Books & Literature',
        'Music & Entertainment': '🎵 Music & Entertainment',
        'Sports & Fitness': '🏃 Sports & Fitness',
        'Sustainability & Environment': '🌱 Sustainability & Environment',
        'Personal Development': '📈 Personal Development',
        'Photography': '📸 Photography',
        'Languages': '🗣️ Languages'
      };
      
      return interests.map(interest => displays[interest] || interest);
    }
  
    getFikaGoalDisplay(goals) {
      if (!goals || !Array.isArray(goals)) return [];
      
      const displays = {
        'Cultural Exchange': '🌍 Cultural Exchange',
        'Making Friends': '👫 Making Friends',
        'Language Practice': '🗣️ Language Practice',
        'Creative Collaboration': '🎨 Creative Collaboration',
        'Mentorship': '🎯 Mentorship',
        'Social Support': '🤝 Social Support'
      };
      
      return goals.map(goal => displays[goal] || goal);
    }

    // 9-26-25
    getLocationPreferenceDisplay(locationPreference) {
      const displayMap = {
        'Shibuya & Harajuku': '🏙️ Shibuya & Harajuku',
        'Ginza & Marunouchi': '🏢 Ginza & Marunouchi', 
        'Omotesando & Aoyama': '✨ Omotesando & Aoyama',
        'Shinjuku': '🌆 Shinjuku',
        'Roppongi': '🌃 Roppongi',
        'Nakameguro': '🌸 Nakameguro',
        'Minato': '⚓ Minato',
        'Flexible': '🔄 Flexible'
      };
      return displayMap[locationPreference] || locationPreference;
    }
    
    getSchedulePreferenceDisplay(schedulePreference) {
      const displayMap = {
        'Weekday Morning': '🌅 Weekday Morning',
        'Weekday Afternoon': '☀️ Weekday Afternoon', 
        'Weekday Evening': '🌆 Weekday Evening',
        'Weekend Morning': '🌄 Weekend Morning',
        'Weekend Afternoon': '☀️ Weekend Afternoon',
        'Weekend Evening': '🌃 Weekend Evening',
        'Flexible': '🔄 Flexible'
      };
      return displayMap[schedulePreference] || schedulePreference;
    }
  
    // =====================================================
    // AI MATCHING ALGORITHMS
    // =====================================================
  
    calculateCompatibilityScore(app1, app2) {
      let score = 0;
      
      // Professional compatibility (25 points)
      if (this.isProfessionallyCompatible(app1, app2)) {
        score += 25;
      }
      
      // Interest overlap (20 points)
      const commonInterests = this.getCommonInterests(app1.interests, app2.interests);
      score += Math.min(commonInterests * 5, 20);
      
      // Fika goals alignment (20 points)
      const commonGoals = this.getCommonInterests(app1.fika_goals, app2.fika_goals);
      score += Math.min(commonGoals * 7, 20);
      
      // Communication style compatibility (15 points)
      if (this.areCommStylesCompatible(app1.communication_style, app2.communication_style)) {
        score += 15;
      }
      
      // Location preference (10 points)
      if (this.areLocationsCompatible(app1.location_preference, app2.location_preference)) {
        score += 10;
      }
      
      // Career stage diversity bonus (10 points)
      if (this.hasCareerStageDiversity(app1.career_stage, app2.career_stage)) {
        score += 10;
      }
      
      return Math.min(score, 100); // Cap at 100%
    }
  
    isProfessionallyCompatible(app1, app2) {
      // Different industries but complementary career stages create interesting dynamics
      const sameIndustry = app1.industry === app2.industry;
      const complementaryStages = this.hasCareerStageDiversity(app1.career_stage, app2.career_stage);
      
      // Entrepreneurs and executives often connect well across industries
      const entrepreneurialMatch = (
        app1.career_stage === 'Entrepreneur' || app2.career_stage === 'Entrepreneur' ||
        app1.career_stage === 'Executive' || app2.career_stage === 'Executive'
      );
      
      return sameIndustry || complementaryStages || entrepreneurialMatch;
    }
  
    getCommonInterests(interests1, interests2) {
      if (!interests1 || !interests2) return 0;
      if (!Array.isArray(interests1) || !Array.isArray(interests2)) return 0;
      
      return interests1.filter(interest => interests2.includes(interest)).length;
    }
  
    areCommStylesCompatible(style1, style2) {
      if (!style1 || !style2) return true; // Neutral if unknown
      
      // Flexible users are compatible with anyone
      if (style1 === 'Flexible' || style2 === 'Flexible') return true;
      
      const compatiblePairs = {
        'Deep Conversations': ['Deep Conversations', 'Professional'],
        'Light & Fun': ['Light & Fun', 'Creative & Artistic'],
        'Professional': ['Professional', 'Deep Conversations'],
        'Creative & Artistic': ['Creative & Artistic', 'Light & Fun', 'Deep Conversations']
      };
      
      return compatiblePairs[style1]?.includes(style2) || false;
    }
      
    origareCommStylesCompatible(style1, style2) {
      if (!style1 || !style2) return true; // Neutral if unknown
      
      const compatiblePairs = {
        'Deep Conversations': ['Deep Conversations', 'Professional'],
        'Light & Fun': ['Light & Fun', 'Creative & Artistic'],
        'Professional': ['Professional', 'Deep Conversations'],
        'Creative & Artistic': ['Creative & Artistic', 'Light & Fun', 'Deep Conversations']
      };
      
      return compatiblePairs[style1]?.includes(style2) || false;
    }

    // Enhanced schedule compatibility checker  
areSchedulesCompatible(schedule1, schedule2) {
  if (!schedule1 || !schedule2) return true;
  
  // Flexible users are compatible with any schedule
  if (schedule1 === 'Flexible' || schedule2 === 'Flexible') return true;
  
  // Same specific schedules are compatible
  return schedule1 === schedule2;
}

// Utility function to identify flexible users for matching
getFlexibleUsers(applications) {
  return applications.filter(app => 
    app.communication_style === 'Flexible' || 
    app.schedule_preference === 'Flexible'
  );
}

// Enhanced group formation that utilizes flexible users
formOptimalGroups(applications) {
  const flexibleUsers = this.getFlexibleUsers(applications);
  const specificUsers = applications.filter(app => !flexibleUsers.includes(app));
  
  // Form core groups with specific preferences first
  const coreGroups = this.formCoreGroups(specificUsers);
  
  // Fill gaps with flexible users
  const filledGroups = this.fillGroupsWithFlexibleUsers(coreGroups, flexibleUsers);
  
  return filledGroups;
}

// Helper function to fill groups with flexible users
fillGroupsWithFlexibleUsers(groups, flexibleUsers) {
  let remainingFlexible = [...flexibleUsers];
  
  // Fill groups that have space (2-4 people ideal)
  groups.forEach(group => {
    while (group.length < 4 && remainingFlexible.length > 0) {
      const flexibleUser = remainingFlexible.shift();
      group.push(flexibleUser);
    }
  });
  
  // Create new groups with remaining flexible users
  while (remainingFlexible.length >= 2) {
    const newGroup = remainingFlexible.splice(0, Math.min(4, remainingFlexible.length));
    groups.push(newGroup);
  }
  
  return groups;
}
  
    areLocationsCompatible(loc1, loc2) {
      if (!loc1 || !loc2) return true;
      if (loc1 === 'Flexible' || loc2 === 'Flexible') return true;
      return loc1 === loc2;
    }
  
    hasCareerStageDiversity(stage1, stage2) {
      if (!stage1 || !stage2) return false;
      
      const stageMap = {
        'Student': 1,
        'Early Career': 2,
        'Mid-Career': 3,
        'Senior Level': 4,
        'Executive': 5,
        'Entrepreneur': 3.5, // Can connect across stages
        'Freelancer': 2.5
      };
      
      const level1 = stageMap[stage1] || 3;
      const level2 = stageMap[stage2] || 3;
      
      // Ideal gap is 1-2 levels (mentorship dynamic)
      const gap = Math.abs(level1 - level2);
      return gap >= 1 && gap <= 2;
    }
  
    findOptimalSessionSize(applications) {
      // Curated Fika focuses on intimate groups
      if (applications.length < 2) return applications.length;
      if (applications.length <= 4) return applications.length;
      
      // For larger sets, prefer groups of 3-4
      return Math.min(4, applications.length);
    }
  
    generateSessionName(applications) {
      if (!applications || applications.length === 0) return 'Curated Fika Session';
      
      // Find common interests or areas
      const allInterests = applications.flatMap(app => app.interests || []);
      const interestCounts = {};
      allInterests.forEach(interest => {
        interestCounts[interest] = (interestCounts[interest] || 0) + 1;
      });
      
      const topInterest = Object.keys(interestCounts).reduce((a, b) => 
        interestCounts[a] > interestCounts[b] ? a : b, null);
      
      const location = applications[0].location_preference || 'Tokyo';
      const month = new Date().toLocaleDateString('en-US', { month: 'long' });
      
      if (topInterest) {
        return `${topInterest} Curated Fika - ${location}`;
      } else {
        return `Curated Fika - ${location} ${month}`;
      }
    }
  
    // =====================================================
    // VALIDATION HELPERS
    // =====================================================
  
    validateSessionCreation(applications) {
      const errors = [];
      
      if (!applications || applications.length < 2) {
        errors.push('Need at least 2 applications to create a curated session');
      }
      
      if (applications.length > 4) {
        errors.push('Curated sessions cannot have more than 4 participants');
      }
      
      // Check if applications are approved and premium eligible
      const nonApproved = applications.filter(app => app.application_status !== 'approved');
      if (nonApproved.length > 0) {
        errors.push(`${nonApproved.length} application(s) are not approved`);
      }
      
      // Check if all applications have premium membership
      const nonPremium = applications.filter(app => !app.is_premium_member);
      if (nonPremium.length > 0) {
        errors.push(`${nonPremium.length} application(s) do not have premium membership`);
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  
    validateApplicationStatusChange(currentStatus, newStatus) {
      const validTransitions = {
        'submitted': ['under_review', 'approved', 'rejected', 'waitlist'],
        'under_review': ['approved', 'rejected', 'waitlist', 'submitted'],
        'approved': ['waitlist', 'under_review'], // Can be moved to waitlist if needed
        'rejected': ['under_review'], // Can be reconsidered
        'waitlist': ['approved', 'rejected', 'under_review']
      };
      
      const allowedStatuses = validTransitions[currentStatus] || [];
      const isValid = allowedStatuses.includes(newStatus);
      
      return {
        isValid,
        message: isValid ? 
          `Status change from ${currentStatus} to ${newStatus} is valid` :
          `Cannot change status from ${currentStatus} to ${newStatus}. Allowed: ${allowedStatuses.join(', ')}`
      };
    }
  
    // =====================================================
    // PREMIUM FEATURES FOR CURATED FIKA
    // =====================================================
  
    calculateSessionPricing(applications, venueType = 'premium') {
      const basePrice = 30.00; // Premium pricing for curated experience
      const venueMultiplier = {
        'premium': 1.0,
        'luxury': 1.5,
        'exclusive': 2.0
      };
      
      const sessionSize = applications.length;
      const sizeDiscount = sessionSize === 2 ? 1.2 : 1.0; // Slight premium for 2-person intimacy
      
      const pricePerPerson = basePrice * (venueMultiplier[venueType] || 1.0) * sizeDiscount;
      
      return {
        pricePerPerson: Math.round(pricePerPerson * 100) / 100,
        totalRevenue: Math.round(pricePerPerson * sessionSize * 100) / 100,
        breakdown: {
          basePrice,
          venueType,
          sessionSize,
          finalPrice: pricePerPerson
        }
      };
    }
  
    suggestOptimalVenue(applications, locationPreference) {
      // This would integrate with venue database in Phase 2
      const venueTypes = {
        'Shibuya & Harajuku': ['Specialty coffee roasters', 'Boutique cafes'],
        'Ginza & Marunouchi': ['Luxury hotel lounges', 'Executive coffee spaces'],
        'Omotesando & Aoyama': ['Design-forward cafes', 'Gallery spaces'],
        'Roppongi': ['International hotel lobbies', 'Sky lounges'],
        'Flexible': ['Rotating premium venues']
      };
      
      return {
        location: locationPreference,
        suggestedVenues: venueTypes[locationPreference] || venueTypes['Flexible'],
        notes: 'Premium venues selected for intimate conversation and exceptional coffee'
      };
    }

  
 


// =====================================================
// ENTREPRENEUR-SPECIFIC METHODS (via Edge Functions)
// =====================================================

async getEntrepreneurApplicationByEmail(email) {
  return await this.callAPI('entrepreneur_get_application_by_email', { email });
}

async getAllEntrepreneurApplications(filters = {}) {
  return await this.callAPI('entrepreneur_get_applications', null, filters);
}

async updateEntrepreneurApplicationStatus(applicationId, status, notes = null) {
  return await this.callAPI('entrepreneur_update_status', {
    applicationId,
    status,
    notes
  });
}

async getMatchingEntrepreneurCandidates(targetTopics = null, targetStage = null, targetLocation = null, excludeIds = []) {
  return await this.callAPI('entrepreneur_get_matching_candidates', {
    targetTopics,
    targetStage,
    targetLocation,
    excludeIds
  });
}


async getEntrepreneurAnalytics() {
  return await this.callAPI('entrepreneur_get_analytics');  // Fixed
}


async getEntrepreneurTopicsPopularity() {
  return await this.callAPI('entrepreneur_get_topics_popularity');
}

async getCompanyStageDistribution() {
  return await this.callAPI('entrepreneur_get_stage_distribution');
}

async deleteEntrepreneurApplication(applicationId) {
  return await this.callAPI('entrepreneur_delete_application', { applicationId });
}

async updateEntrepreneurCompatibilityScore(applicationId, score, tags = []) {
  return await this.callAPI('entrepreneur_update_compatibility_score', {
    applicationId,
    score,
    tags
  });
}

    // end 11-9-25


    // Session Management
    // 11-16-25

    async getEntrepreneurAnalytics() {
      return await this.callAPI('entrepreneur_get_analytics');  // Fixed
    }

   
    async getEntrepreneurSessions(filters = {}) {
      return await this.callAPI('entrepreneur_get_sessions', null, filters);  // Fixed
    }

    async getEntrepreneurSessionDetails(sessionId) {
      return await this.callAPI('entrepreneur_get_session_details', { sessionId });  // Fixed
    }


    async sendEntrepreneurSessionInvitations(sessionId) {
      return await this.callAPI('entrepreneur_send_invitations', {
        sessionId
      });
    }


async createEntrepreneurSessionFromApplications(applicationIds, sessionData) {
  if (!applicationIds || applicationIds.length !== 2) {
    return {
      success: false,
      error: 'Entrepreneur 1-on-1 requires exactly 2 applications'
    };
  }
  
  return await this.callAPI('entrepreneur_create_session', {  // ← CHANGE THIS
    applicationIds,
    sessionData
  });
}


async updateEntrepreneurSessionDetails(sessionId, updates) {
  return await this.callAPI('entrepreneur_update_session', {  // FIXED
    sessionId,
    updates
  });
}

async updateEntrepreneurSessionStatus(sessionId, status) {
  return await this.callAPI('entrepreneur_update_session_status', {
    sessionId,
    status
  });
}

async deleteEntrepreneurSession(sessionId, options = {}) {
  return await this.callAPI('entrepreneur_delete_session', {  // FIXED
    sessionId,
    options
  });
}

async addParticipantToEntrepreneurSession(sessionId, applicationId, options = {}) {
  return await this.callAPI('entrepreneur_add_participant', {  // FIXED
    sessionId,
    applicationId,
    options
  });
}

async deleteEntrepreneurParticipant(participantId, options = {}) {
  return await this.callAPI('entrepreneur_delete_participant', {  // FIXED
    participantId,
    options
  });
}

    // 

  }


  
  
  // Export singleton instance
  const curatedFikaAdminService = new CuratedFikaAdminService();
  export default curatedFikaAdminService;